import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  STATUS_TRANSITIONS,
  STATUS_COLORS,
  STATUSES,
  ROLES,
} from '../../config/constants';

/**
 * Filter available transitions based on the user role.
 * - Bankers can only transition to certain statuses.
 * - Associates have limited transitions.
 * - Admins and managers have full access to all defined transitions.
 */
const getFilteredTransitions = (currentStatus, userRole) => {
  const allTransitions = STATUS_TRANSITIONS[currentStatus] || [];

  if (userRole === ROLES.BANKER) {
    // Bankers can only handle review-related transitions
    const bankerAllowed = [
      STATUSES.UNDER_REVIEW,
      STATUSES.QUERY_RAISED,
      STATUSES.APPROVED,
      STATUSES.REJECTED,
      STATUSES.SANCTIONED,
      STATUSES.DISBURSED,
    ];
    return allTransitions.filter((s) => bankerAllowed.includes(s));
  }

  if (userRole === ROLES.ASSOCIATE) {
    // Associates cannot cancel or reject; they handle doc-level transitions
    const associateBlocked = [STATUSES.REJECTED];
    return allTransitions.filter((s) => !associateBlocked.includes(s));
  }

  // Admin / Manager get all transitions
  return allTransitions;
};

const StatusTransitionDialog = ({
  open,
  onClose,
  currentStatus,
  onSubmit,
  userRole,
  loading = false,
}) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');

  const availableStatuses = useMemo(
    () => getFilteredTransitions(currentStatus, userRole),
    [currentStatus, userRole]
  );

  const handleSubmit = () => {
    if (!selectedStatus) return;
    onSubmit({ status: selectedStatus, notes: notes.trim() });
  };

  const handleClose = () => {
    setSelectedStatus('');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Status</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <Chip
            label={currentStatus}
            size="small"
            sx={{
              backgroundColor: STATUS_COLORS[currentStatus] || '#9e9e9e',
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </Box>

        {availableStatuses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No transitions available from this status for your role.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select New Status
            </Typography>
            <RadioGroup
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {availableStatuses.map((status) => (
                <FormControlLabel
                  key={status}
                  value={status}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: STATUS_COLORS[status] || 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: STATUS_COLORS[status] || '#9e9e9e',
                        }}
                      />
                      <Typography variant="body2">{status}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Notes (optional)"
              placeholder="Add any notes about this status change..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedStatus || loading}
        >
          {loading ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusTransitionDialog;
